// Tek sayfada Ülke / Şehir / İlçe yönetimi.
// Paylaşılan UI bileşenleri kullanılır (Modal, Card, FormField, Button, EmptyState).
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { TabIcon } from '../utils/tabIcons';
import { Button, Modal, Card, FormField, EmptyState, ListPageHeader } from '../components/ui';

export default function LocationsPage() {
  const [activeTab, setActiveTab] = useState('countries');
  const [msg, setMsg] = useState(null);
  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 2500); };

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <ListPageHeader title="Yer Tanımları" count="Ülke / Şehir / İlçe" />

      {msg && <Toast {...msg} />}

      <div className="detail-tabs" style={{ flexShrink: 0 }}>
        <button className={'tab-btn' + (activeTab === 'countries' ? ' active' : '')} onClick={() => setActiveTab('countries')}>
          <TabIcon name="flag" /><span>Ülkeler</span>
        </button>
        <button className={'tab-btn' + (activeTab === 'cities' ? ' active' : '')} onClick={() => setActiveTab('cities')}>
          <TabIcon name="city" /><span>Şehirler</span>
        </button>
        <button className={'tab-btn' + (activeTab === 'districts' ? ' active' : '')} onClick={() => setActiveTab('districts')}>
          <TabIcon name="pin" /><span>İlçeler</span>
        </button>
      </div>

      <div style={{
        padding: 20, maxWidth: 1100, margin: '0 auto', width: '100%', boxSizing: 'border-box',
        flex: 1, minHeight: 0, overflowY: 'auto',
      }}>
        {activeTab === 'countries' && <CountriesTab onMsg={showMsg} />}
        {activeTab === 'cities'    && <CitiesTab onMsg={showMsg} />}
        {activeTab === 'districts' && <DistrictsTab onMsg={showMsg} />}
      </div>
    </div>
  );
}

// ── ÜLKELER ───────────────────────────────────────────────────────────────────
function CountriesTab({ onMsg }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/locations/countries'); if (r.data.success) setItems(r.data.data || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name?.trim()) { onMsg('error', 'Ülke adı zorunlu'); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put('/locations/countries/' + editing.id, editing);
      else await api.post('/locations/countries', editing);
      onMsg('ok', editing.id ? 'Güncellendi' : 'Eklendi'); setEditing(null); load();
    } catch (e) { onMsg('error', e.response?.data?.message || 'Hata'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Silmek istiyor musunuz?')) return;
    try { await api.delete('/locations/countries/' + id); onMsg('ok', 'Silindi'); load(); }
    catch (e) { onMsg('error', e.response?.data?.message || 'Hata'); }
  };

  return (
    <Card title="Ülkeler" count={items.length}
      action={<Button icon={<PlusIcon/>} onClick={() => setEditing({ code: '', name: '', sort_order: 0 })}>Yeni Ülke</Button>}
      noPadding>
      <DataList loading={loading} emptyTitle="Ülke yok" emptyDesc="Henüz ülke eklenmemiş"
        columns={['Kod', 'Ülke Adı', 'Sıra']} widths={[80, '1fr', 80]}
        rows={items.map(it => ({
          key: it.id,
          cells: [
            <code className="loc-code">{it.code || '—'}</code>,
            <strong>{it.name}</strong>,
            <span className="loc-dim">{it.sort_order}</span>,
          ],
          actions: <RowActions onEdit={() => setEditing({ ...it })} onDelete={() => remove(it.id)} />
        }))}
      />
      {editing && (
        <Modal title={editing.id ? 'Ülkeyi Düzenle' : 'Yeni Ülke'} size="sm"
          onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <FormField label="Kod (ISO-2)" hint="TR, US, DE...">
            <input value={editing.code || ''} onChange={e => setEditing({ ...editing, code: e.target.value.toUpperCase() })} maxLength={3} />
          </FormField>
          <FormField label="Ülke Adı" required>
            <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} autoFocus />
          </FormField>
          <FormField label="Sıra">
            <input type="number" value={editing.sort_order || 0} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
          </FormField>
        </Modal>
      )}
    </Card>
  );
}

// ── ŞEHİRLER ──────────────────────────────────────────────────────────────────
function CitiesTab({ onMsg }) {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/locations/countries').then(r => {
      if (r.data.success) {
        setCountries(r.data.data || []);
        if (!filterCountry && r.data.data?.length) setFilterCountry(r.data.data[0].id);
      }
    });
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterCountry ? { country_id: filterCountry } : {};
      const r = await api.get('/locations/cities', { params });
      if (r.data.success) setItems(r.data.data || []);
    } finally { setLoading(false); }
  }, [filterCountry]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.country_id || !editing.name?.trim()) { onMsg('error', 'Ülke ve ad zorunlu'); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put('/locations/cities/' + editing.id, editing);
      else await api.post('/locations/cities', editing);
      onMsg('ok', editing.id ? 'Güncellendi' : 'Eklendi'); setEditing(null); load();
    } catch (e) { onMsg('error', e.response?.data?.message || 'Hata'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Silmek istiyor musunuz?')) return;
    try { await api.delete('/locations/cities/' + id); onMsg('ok', 'Silindi'); load(); }
    catch (e) { onMsg('error', e.response?.data?.message || 'Hata'); }
  };

  return (
    <Card title="Şehirler" count={items.length} noPadding
      filter={
        <select className="ui-input" style={{ minWidth: 180 }} value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
          <option value="">Tüm Ülkeler</option>
          {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      }
      action={<Button icon={<PlusIcon/>} onClick={() => setEditing({ country_id: filterCountry || '', code: '', name: '', sort_order: 0 })}>Yeni Şehir</Button>}>
      <DataList loading={loading} emptyTitle="Şehir yok" emptyDesc="Bu ülkede şehir tanımlanmamış"
        columns={['Kod', 'Şehir', 'Ülke', 'Sıra']} widths={[70, '1fr', '1fr', 70]}
        rows={items.map(it => ({
          key: it.id,
          cells: [
            <code className="loc-code">{it.code || '—'}</code>,
            <strong>{it.name}</strong>,
            <span className="loc-dim">{it.country_name}</span>,
            <span className="loc-dim">{it.sort_order}</span>,
          ],
          actions: <RowActions onEdit={() => setEditing({ ...it })} onDelete={() => remove(it.id)} />
        }))}
      />
      {editing && (
        <Modal title={editing.id ? 'Şehri Düzenle' : 'Yeni Şehir'} size="sm"
          onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <FormField label="Ülke" required>
            <select value={editing.country_id || ''} onChange={e => setEditing({ ...editing, country_id: e.target.value })}>
              <option value="">Seçin...</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="Kod (Plaka vb.)">
            <input value={editing.code || ''} onChange={e => setEditing({ ...editing, code: e.target.value })} maxLength={10} />
          </FormField>
          <FormField label="Şehir Adı" required>
            <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} autoFocus />
          </FormField>
          <FormField label="Sıra">
            <input type="number" value={editing.sort_order || 0} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
          </FormField>
        </Modal>
      )}
    </Card>
  );
}

// ── İLÇELER ───────────────────────────────────────────────────────────────────
function DistrictsTab({ onMsg }) {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [editing, setEditing] = useState(null);
  const [editCities, setEditCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/locations/countries').then(r => {
      if (r.data.success) {
        const list = r.data.data || [];
        setCountries(list);
        if (list.length) setFilterCountry(list[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (!filterCountry) { setCities([]); setFilterCity(''); return; }
    api.get('/locations/cities', { params: { country_id: filterCountry } }).then(r => {
      if (r.data.success) {
        const list = r.data.data || [];
        setCities(list);
        setFilterCity(list[0]?.id || '');
      }
    });
  }, [filterCountry]);

  const load = useCallback(async () => {
    if (!filterCity) { setItems([]); return; }
    setLoading(true);
    try { const r = await api.get('/locations/districts', { params: { city_id: filterCity } });
      if (r.data.success) setItems(r.data.data || []); }
    finally { setLoading(false); }
  }, [filterCity]);
  useEffect(() => { load(); }, [load]);

  const loadCitiesForEdit = async (countryId) => {
    if (!countryId) { setEditCities([]); return []; }
    const r = await api.get('/locations/cities', { params: { country_id: countryId } });
    return r.data.success ? (r.data.data || []) : [];
  };

  const openNew = async () => {
    if (!filterCity || !filterCountry) { onMsg('error', 'Önce ülke ve şehir seçin'); return; }
    const list = await loadCitiesForEdit(filterCountry);
    setEditCities(list);
    setEditing({ country_id: filterCountry, city_id: filterCity, name: '', sort_order: 0 });
  };
  const openEdit = async (it) => {
    const list = await loadCitiesForEdit(it.country_id);
    setEditCities(list);
    setEditing({ ...it });
  };
  const onCountryChange = async (countryId) => {
    const list = await loadCitiesForEdit(countryId);
    setEditCities(list);
    setEditing(prev => ({ ...prev, country_id: countryId, city_id: list[0]?.id || '' }));
  };

  const save = async () => {
    if (!editing.city_id || !editing.name?.trim()) { onMsg('error', 'Şehir ve ad zorunlu'); return; }
    setSaving(true);
    try {
      const payload = { city_id: editing.city_id, name: editing.name, sort_order: editing.sort_order };
      if (editing.id) await api.put('/locations/districts/' + editing.id, payload);
      else await api.post('/locations/districts', payload);
      onMsg('ok', editing.id ? 'Güncellendi' : 'Eklendi'); setEditing(null); load();
    } catch (e) { onMsg('error', e.response?.data?.message || 'Hata'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Silmek istiyor musunuz?')) return;
    try { await api.delete('/locations/districts/' + id); onMsg('ok', 'Silindi'); load(); }
    catch (e) { onMsg('error', e.response?.data?.message || 'Hata'); }
  };

  return (
    <Card title="İlçeler" count={items.length} noPadding
      filter={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="ui-input" style={{ minWidth: 160 }} value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="ui-input" style={{ minWidth: 160 }} value={filterCity} onChange={e => setFilterCity(e.target.value)}>
            {cities.length === 0 && <option value="">— Şehir yok —</option>}
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      }
      action={<Button icon={<PlusIcon/>} disabled={!filterCity} onClick={openNew}>Yeni İlçe</Button>}>
      <DataList loading={loading} emptyTitle="İlçe yok" emptyDesc="Bu şehirde ilçe tanımlanmamış"
        columns={['İlçe Adı', 'Şehir', 'Sıra']} widths={['1fr', '1fr', 70]}
        rows={items.map(it => ({
          key: it.id,
          cells: [
            <strong>{it.name}</strong>,
            <span className="loc-dim">{it.city_name}</span>,
            <span className="loc-dim">{it.sort_order}</span>,
          ],
          actions: <RowActions onEdit={() => openEdit(it)} onDelete={() => remove(it.id)} />
        }))}
      />
      {editing && (
        <Modal title={editing.id ? 'İlçeyi Düzenle' : 'Yeni İlçe'} size="sm"
          onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <FormField label="Ülke" required>
            <select value={editing.country_id || ''} onChange={e => onCountryChange(e.target.value)}>
              <option value="">Seçin...</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="Şehir" required>
            <select value={editing.city_id || ''} onChange={e => setEditing({ ...editing, city_id: e.target.value })}>
              {editCities.length === 0 && <option value="">— Önce ülke seçin —</option>}
              {editCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="İlçe Adı" required>
            <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} autoFocus />
          </FormField>
          <FormField label="Sıra">
            <input type="number" value={editing.sort_order || 0} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
          </FormField>
        </Modal>
      )}
    </Card>
  );
}

// ── DataList — basit grid tablo (DataTable kadar gelişmiş değil, küçük listeler için) ──
function DataList({ loading, emptyTitle, emptyDesc, columns, widths, rows }) {
  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>Yükleniyor...</div>;
  if (rows.length === 0) return <EmptyState title={emptyTitle} description={emptyDesc} compact />;
  const tpl = widths.map(w => typeof w === 'number' ? w + 'px' : w).join(' ') + ' 110px';
  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: tpl, gap: 12,
        padding: '10px 18px', fontSize: 'var(--text-xs)', textTransform: 'uppercase',
        letterSpacing: '0.04em', fontWeight: 700, color: 'var(--text-tertiary)',
        borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)',
      }}>
        {columns.map((c, i) => <div key={i}>{c}</div>)}
        <div style={{ textAlign: 'right' }}>İşlem</div>
      </div>
      {rows.map(r => (
        <div key={r.key} style={{
          display: 'grid', gridTemplateColumns: tpl, gap: 12,
          padding: '12px 18px', alignItems: 'center', fontSize: 'var(--text-sm)',
          borderBottom: '1px solid var(--border-secondary)', transition: 'background 0.12s',
        }}
          onMouseEnter={e => e.currentTarget.style.background='var(--bg-card-hover)'}
          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
          {r.cells.map((cell, i) => <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell}</div>)}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>{r.actions}</div>
        </div>
      ))}
    </div>
  );
}

function RowActions({ onEdit, onDelete }) {
  return (
    <>
      <Button variant="ghost" size="sm" iconOnly icon={<EditIcon/>} onClick={onEdit} title="Düzenle" />
      <Button variant="danger" size="sm" iconOnly icon={<TrashIcon/>} onClick={onDelete} title="Sil" />
    </>
  );
}

const PlusIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const EditIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>;
const TrashIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>;

function Toast({ type, text }) {
  return (
    <div style={{
      position: 'fixed', top: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 1500,
      padding: '10px 18px', borderRadius: 'var(--r-md)', fontSize: 'var(--text-sm)', fontWeight: 500,
      background: type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.12)',
      color: type === 'error' ? 'var(--danger)' : '#059669',
      border: '1px solid ' + (type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'),
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    }}>{text}</div>
  );
}
